import { defineComponent as _defineComponent } from 'vue';
import RecordBasicInfo from '../../RecordBasicInfo.vue';
import { reactive, watchEffect } from 'vue';
import useVuelidate from '@vuelidate/core';
import { DateTime } from 'luxon';
import { required, decimal, integer, minValue, maxValue, } from '@vuelidate/validators';
import SaveCancelButtons from '../../../../Components/SaveCancelButtons.vue';
import InputNumberFloatLabel from '../../../../Components/InputNumber.vue';
import { trans } from 'laravel-vue-i18n';
import { processDataForm } from '../../../../Services/Utils';
import FertilizationService from '../../../../Services/FertilizationService';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputNumber from 'primevue/inputnumber';
import Message from 'primevue/message';
export default /*#__PURE__*/ _defineComponent({
    __name: 'EditCreate',
    props: {
        data: {
            type: Object,
            default: {},
        },
    },
    emits: ['cancel', 'saved'],
    setup(__props, { expose, emit }) {
        expose();
        const props = __props;
        const fertilizationService = new FertilizationService();
        const atLeastOneRate = () => numericFields.reduce((acc, nf) => acc + data[nf.field + '_rate'], 0) > 0;
        const sumLimit100 = () => numericFields.reduce((acc, nf) => acc + data[nf.field + '_rate'], 0) <= 100;
        const getRate = (field) => {
            if (!props.data.fertilizer_quantity || !props.data[field]) {
                return 0;
            }
            return (props.data[field] / props.data.fertilizer_quantity) * 100;
        };
        const data = reactive({
            id: props.data.id,
            orchard_id: props.data.orchard?.id,
            plot_ids: [],
            date: (props.data.date
                ? DateTime.fromISO(props.data.date)
                : DateTime.utc()).toJSDate(),
            fertilizer_quantity: props.data.fertilizer_quantity,
            nitrogen: props.data.nitrogen || 0,
            phosphorus: props.data.phosphorus || 0,
            potassium: props.data.potassium || 0,
            calcium: props.data.calcium || 0,
            magnesium: props.data.magnesium || 0,
            sulfur: props.data.sulfur || 0,
            iron: props.data.iron || 0,
            manganese: props.data.manganese || 0,
            zinc: props.data.zinc || 0,
            copper: props.data.copper || 0,
            boron: props.data.boron || 0,
            nitrogen_rate: getRate('nitrogen'),
            phosphorus_rate: getRate('phosphorus'),
            potassium_rate: getRate('potassium'),
            calcium_rate: getRate('calcium'),
            magnesium_rate: getRate('magnesium'),
            sulfur_rate: getRate('sulfur'),
            iron_rate: getRate('iron'),
            manganese_rate: getRate('manganese'),
            zinc_rate: getRate('zinc'),
            copper_rate: getRate('copper'),
            boron_rate: getRate('boron'),
        });
        const rules = {
            orchard_id: { required },
            plot_ids: { required },
            date: { required },
            fertilizer_quantity: { required, integer, minValue: minValue(1) },
            nitrogen: { required, decimal },
            phosphorus: { required, decimal },
            potassium: { required, decimal },
            calcium: { required, decimal },
            magnesium: { required, decimal },
            sulfur: { required, decimal },
            iron: { required, decimal },
            manganese: { required, decimal },
            zinc: { required, decimal },
            copper: { required, decimal },
            boron: { required, decimal },
            nitrogen_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            phosphorus_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            potassium_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            calcium_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            magnesium_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            sulfur_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            iron_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            manganese_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            zinc_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            copper_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
            boron_rate: {
                required,
                integer,
                atLeastOneRate,
                sumLimit100,
                minValue: minValue(0),
                maxValue: maxValue(100),
            },
        };
        const v$ = useVuelidate(rules, data);
        const numericFields = [
            { field: 'nitrogen', unit: 'kg/ha', label: '' },
            { field: 'phosphorus', unit: 'kg/ha', label: '' },
            { field: 'potassium', unit: 'kg/ha', label: '' },
            { field: 'calcium', unit: 'kg/ha', label: '' },
            { field: 'magnesium', unit: 'kg/ha', label: '' },
            { field: 'sulfur', unit: 'kg/ha', label: '' },
            { field: 'iron', unit: 'kg/ha', label: '' },
            { field: 'manganese', unit: 'kg/ha', label: '' },
            { field: 'zinc', unit: 'kg/ha', label: '' },
            { field: 'copper', unit: 'kg/ha', label: '' },
            { field: 'boron', unit: 'kg/ha', label: '' },
        ];
        numericFields.map((nf) => {
            nf.label = trans('labels.' + nf.field);
        });
        numericFields.sort((a, b) => a.label.localeCompare(b.label));
        const save = async () => {
            const valid = await v$.value.$validate();
            if (!valid) {
                return;
            }
            const formData = processDataForm(data);
            fertilizationService.save(formData).then((data) => emit('saved', data));
        };
        watchEffect(() => {
            numericFields.forEach((nf) => {
                data[nf.field] = data.fertilizer_quantity
                    ? (data[nf.field + '_rate'] * data.fertilizer_quantity) / 100
                    : 0;
            });
        });
        const __returned__ = { fertilizationService, props, emit, atLeastOneRate, sumLimit100, getRate, data, rules, v$, numericFields, save, RecordBasicInfo, SaveCancelButtons, InputNumberFloatLabel, get trans() { return trans; }, get DataTable() { return DataTable; }, get Column() { return Column; }, get InputNumber() { return InputNumber; }, get Message() { return Message; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
